import { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import useLogActivityAccess from '../hooks/useLogActivityAccess';

async function playTTS(text, voiceName, languageCode) {
  try {
    const res = await fetch('/synt/text-to-speech', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        text,
        languageCode: languageCode || 'en-US',
        voiceName: voiceName || 'en-US-Neural2-J',
        pitch: 0,
      }),
      credentials: 'include',
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audio.play();
  } catch {}
}

async function translateText(text) {
  const res = await fetch('/traduzir/traduzir', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ texto: text, idiomaOrigem: 'en', idiomaDestino: 'pt' }),
    credentials: 'include',
  });
  const data = await res.json();
  return data.textoTraduzido || '';
}

function CharacterCard({ char, onSelect }) {
  const grad = `linear-gradient(135deg, ${char.gradient[0]}, ${char.gradient[1]})`;
  return (
    <div
      onClick={() => onSelect(char)}
      style={{
        background: grad, borderRadius: 18, padding: '22px 18px', cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s', boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(0,0,0,0.35)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.25)'; }}
    >
      <div style={{ fontSize: 40, textAlign: 'center' }}>{char.avatar}</div>
      <div style={{ color: '#fff', fontWeight: 700, fontSize: 18, textAlign: 'center' }}>{char.name}</div>
      <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, textAlign: 'center' }}>
        {char.age} anos · {char.origin}
      </div>
      <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: 8, padding: '4px 10px', color: '#fff', fontSize: 11, textAlign: 'center', fontWeight: 600 }}>
        {char.occupation}
      </div>
      <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>
        {char.description}
      </div>
    </div>
  );
}

function ChatMessage({ msg, character }) {
  const isUser = msg.role === 'user';
  const [translation, setTranslation] = useState(null);
  const [translating, setTranslating] = useState(false);

  async function handleTranslate() {
    if (translation) { setTranslation(null); return; }
    setTranslating(true);
    try {
      const t = await translateText(msg.content);
      setTranslation(t);
    } catch {}
    setTranslating(false);
  }

  const btnBase = {
    border: 'none', borderRadius: 20, padding: '4px 12px',
    fontSize: 12, cursor: 'pointer', marginTop: 6,
  };

  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: 12, gap: 8, alignItems: 'flex-end' }}>
      {!isUser && <div style={{ fontSize: 26, flexShrink: 0 }}>{character.avatar}</div>}
      <div style={{
        maxWidth: '72%',
        background: isUser ? 'linear-gradient(135deg, #1e90ff, #0052cc)' : 'rgba(255,255,255,0.08)',
        border: isUser ? 'none' : '1px solid rgba(255,255,255,0.12)',
        borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
        padding: '12px 16px', color: '#fff', fontSize: 15, lineHeight: 1.55,
      }}>
        {msg.content}
        {translation && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', fontSize: 13, lineHeight: 1.5 }}>
            🇧🇷 {translation}
          </div>
        )}
        {!isUser && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            <button
              onClick={() => playTTS(msg.content, character.voiceName, character.languageCode)}
              style={{ ...btnBase, background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}
            >
              🔊 Ouvir
            </button>
            <button
              onClick={handleTranslate}
              disabled={translating}
              style={{ ...btnBase, background: translation ? 'rgba(255,255,255,0.18)' : 'rgba(30,200,100,0.18)', color: translating ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.8)' }}
            >
              {translating ? '...' : translation ? '✕ PT' : '🇧🇷 PT'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Voice Mode ─────────────────────────────────────────────────────────────────

function VoiceMessage({ transcript, reply, character }) {
  const [translation, setTranslation] = useState(null);
  const [translating, setTranslating] = useState(false);
  const btnBase = { border: 'none', borderRadius: 20, padding: '4px 12px', fontSize: 12, cursor: 'pointer', marginTop: 6 };

  async function handleTranslate() {
    if (translation) { setTranslation(null); return; }
    setTranslating(true);
    try { setTranslation(await translateText(reply)); } catch {}
    setTranslating(false);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
      {/* User transcript */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ maxWidth: '72%', background: 'linear-gradient(135deg, #1e90ff, #0052cc)', borderRadius: '18px 18px 4px 18px', padding: '12px 16px', color: '#fff', fontSize: 15 }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>🎙 Você disse:</div>
          {transcript}
        </div>
      </div>
      {/* AI reply */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
        <div style={{ fontSize: 26, flexShrink: 0 }}>{character.avatar}</div>
        <div style={{ maxWidth: '72%', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '18px 18px 18px 4px', padding: '12px 16px', color: '#fff', fontSize: 15, lineHeight: 1.55 }}>
          {reply}
          {translation && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
              🇧🇷 {translation}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            <button onClick={() => playTTS(reply, character.voiceName, character.languageCode)} style={{ ...btnBase, background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}>
              🔊 Ouvir
            </button>
            <button onClick={handleTranslate} disabled={translating} style={{ ...btnBase, background: translation ? 'rgba(255,255,255,0.18)' : 'rgba(30,200,100,0.18)', color: translating ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.8)' }}>
              {translating ? '...' : translation ? '✕ PT' : '🇧🇷 PT'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function audioBufferToWav(audioBuffer) {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const bitDepth = 16;
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;
  const dataLength = audioBuffer.length * blockAlign;
  const buffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer);

  function writeString(offset, value) {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  }

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(36, 'data');
  view.setUint32(40, dataLength, true);

  let offset = 44;
  const channelData = Array.from({ length: numChannels }, (_, channel) => audioBuffer.getChannelData(channel));

  for (let i = 0; i < audioBuffer.length; i += 1) {
    for (let channel = 0; channel < numChannels; channel += 1) {
      const sample = Math.max(-1, Math.min(1, channelData[channel][i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

function pcmToWavBlob(chunks, sampleRate) {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const buffer = new ArrayBuffer(44 + totalLength * 2);
  const view = new DataView(buffer);

  function writeString(offset, value) {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  }

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + totalLength * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, totalLength * 2, true);

  let offset = 44;
  for (const chunk of chunks) {
    for (let i = 0; i < chunk.length; i += 1) {
      const sample = Math.max(-1, Math.min(1, chunk[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

function VoiceMode({ character }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [exchanges, setExchanges] = useState([]);
  const [error, setError] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [exchanges, processing]);

  // Play audio via AudioContext — created/resumed during user gesture so autoplay policy is satisfied
  async function playAudio(arrayBuffer) {
    try {
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      if (ctx.state === 'suspended') await ctx.resume();
      const decoded = await ctx.decodeAudioData(arrayBuffer);
      const source = ctx.createBufferSource();
      source.buffer = decoded;
      source.connect(ctx.destination);
      source.start(0);
    } catch (e) {
      console.warn('[TTS playback]', e.message);
    }
  }

  async function startRecording() {
    setError(null);
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      await audioCtxRef.current.resume();
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          sampleRate: 44100,
        },
      });

      const audioCtx = audioCtxRef.current;
      const source = audioCtx.createMediaStreamSource(stream);
      const processor = audioCtx.createScriptProcessor(4096, 1, 1);
      const silentGain = audioCtx.createGain();
      silentGain.gain.value = 0;
      const pcmChunks = [];

      processor.onaudioprocess = e => {
        const input = e.inputBuffer.getChannelData(0);
        const output = new Int16Array(input.length);
        let peak = 0;

        for (let i = 0; i < input.length; i += 1) {
          const sample = Math.max(-1, Math.min(1, input[i]));
          output[i] = sample * 0x8000;
          if (Math.abs(sample) > peak) {
            peak = Math.abs(sample);
          }
        }

        pcmChunks.push(output);
      };

      source.connect(processor);
      processor.connect(silentGain);
      silentGain.connect(audioCtx.destination);

      chunksRef.current = pcmChunks;
      mediaRef.current = { stream, source, processor, startedAt: Date.now() };
      setRecording(true);
    } catch {
      setError('Não foi possível acessar o microfone. Verifique as permissões do navegador.');
    }
  }

  async function stopRecording() {
    setRecording(false);
    const ref = mediaRef.current;
    if (!ref) return;

    const { stream, source, processor, startedAt } = ref;
    mediaRef.current = null;
    source.disconnect();
    processor.disconnect();
    stream.getTracks().forEach(t => t.stop());

    const durationMs = Date.now() - startedAt;
    const wavBlob = pcmToWavBlob(chunksRef.current, 44100);

    if (wavBlob.size < 20000 || durationMs < 800) {
      setError('Gravação muito curta. Fale por pelo menos 1 segundo e tente novamente.');
      return;
    }

    await sendAudio(wavBlob, 'audio/wav');
  }

  async function sendAudio(blob, mimeType = 'audio/wav') {
    setProcessing(true);
    try {
      const form = new FormData();
      const extension = mimeType.includes('mp4') ? 'm4a' : mimeType.includes('wav') ? 'wav' : 'webm';
      form.append('audio', blob, `audio.${extension}`);
      form.append('characterId', character.id);
      const res = await fetch('/api/chat/voice', { method: 'POST', body: form, credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro desconhecido');
      setExchanges(prev => [...prev, { transcript: data.transcript, reply: data.reply }]);

      // Fetch TTS audio and play via pre-unlocked AudioContext
      const ttsRes = await fetch('/synt/text-to-speech', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: data.reply,
          languageCode: character.languageCode,
          voiceName: character.voiceName,
          pitch: 0,
        }),
        credentials: 'include',
      });
      if (!ttsRes.ok) {
        const errText = await ttsRes.text();
        console.error('[TTS]', ttsRes.status, errText);
        // Text already shown — skip audio without blocking
      } else {
        const audioArrayBuffer = await ttsRes.arrayBuffer();
        await playAudio(audioArrayBuffer);
      }
    } catch (err) {
      setError(err.message || 'Erro ao processar voz.');
    } finally {
      setProcessing(false);
    }
  }

  const grad = `linear-gradient(135deg, ${character.gradient[0]}, ${character.gradient[1]})`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 8 }}>
        {exchanges.length === 0 && !processing && (
          <div style={{ textAlign: 'center', opacity: 0.45, marginTop: 60, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 40 }}>{character.avatar}</div>
            <div style={{ fontSize: 14 }}>Pressione o microfone e fale em inglês!</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{character.name} vai responder com a própria voz</div>
          </div>
        )}
        {exchanges.map((ex, i) => (
          <VoiceMessage key={i} transcript={ex.transcript} reply={ex.reply} character={character} />
        ))}
        {processing && (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 26 }}>{character.avatar}</div>
            <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '18px 18px 18px 4px', padding: '14px 18px' }}>
              <span style={{ display: 'inline-block', animation: 'pulse 1s infinite' }}>✦ ✦ ✦</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '8px 14px', marginBottom: 10, fontSize: 13, color: '#fca5a5' }}>
          ⚠ {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12 }}>
        <button
          onMouseDown={startRecording}
          onMouseUp={stopRecording}
          onTouchStart={e => { e.preventDefault(); startRecording(); }}
          onTouchEnd={e => { e.preventDefault(); stopRecording(); }}
          disabled={processing}
          style={{
            width: 80, height: 80, borderRadius: '50%', border: 'none', cursor: processing ? 'default' : 'pointer',
            background: recording ? '#ef4444' : processing ? 'rgba(255,255,255,0.1)' : grad,
            color: '#fff', fontSize: 28, boxShadow: recording ? '0 0 0 12px rgba(239,68,68,0.25)' : '0 4px 20px rgba(0,0,0,0.4)',
            transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {processing ? '⏳' : recording ? '⏹' : '🎙'}
        </button>
      </div>
      <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: 11, marginTop: 8 }}>
        {recording ? 'Solte para enviar' : processing ? 'Processando...' : 'Segure para falar'}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function ConversaIA() {
  useLogActivityAccess('conversa-ia');
  const { user } = useAuth();

  const [characters, setCharacters] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingChars, setLoadingChars] = useState(true);
  const [mode, setMode] = useState('text'); // 'text' | 'voice'
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get('/chat/characters')
      .then(r => setCharacters(r.data))
      .catch(() => {})
      .finally(() => setLoadingChars(false));
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  function selectCharacter(char) {
    setSelected(char);
    setMessages([]);
    setInput('');
    setMode('text');
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading || !selected) return;
    const newMessages = [...messages, { role: 'user', content: text }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    try {
      const { data } = await api.post('/chat/message', { characterId: selected.id, messages: newMessages });
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, something went wrong. Try again!" }]);
    } finally {
      setLoading(false);
    }
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  }

  const pageStyle = {
    minHeight: '100vh', background: 'linear-gradient(135deg, #0a0f1e 0%, #0d1b2a 100%)',
    color: '#fff', padding: '32px 16px 60px',
  };
  const innerStyle = { maxWidth: 900, margin: '0 auto' };

  if (!selected) {
    return (
      <div style={pageStyle}>
        <div style={innerStyle}>
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>💬</div>
            <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>Bora Conversar?</h1>
            <p style={{ color: 'rgba(255,255,255,0.55)', marginTop: 8, fontSize: 15 }}>
              Escolha um personagem e pratique inglês em uma conversa natural
            </p>
          </div>
          {loadingChars ? (
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', marginTop: 60 }}>Carregando personagens...</div>
          ) : (
            <>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Homens</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
                {characters.filter(c => c.gender === 'male').map(c => <CharacterCard key={c.id} char={c} onSelect={selectCharacter} />)}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Mulheres</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
                {characters.filter(c => c.gender === 'female').map(c => <CharacterCard key={c.id} char={c} onSelect={selectCharacter} />)}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  const grad = `linear-gradient(135deg, ${selected.gradient[0]}, ${selected.gradient[1]})`;
  const hasVoice = user?.voz_ia === 1;

  return (
    <div style={pageStyle}>
      <div style={{ ...innerStyle, maxWidth: 700 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
          <button
            onClick={() => setSelected(null)}
            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 10, padding: '8px 14px', color: '#fff', cursor: 'pointer', fontSize: 14 }}
          >
            ← Voltar
          </button>
          <div style={{ background: grad, borderRadius: 50, width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>
            {selected.avatar}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 17 }}>{selected.name}</div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{selected.occupation} · {selected.origin}</div>
          </div>
        </div>

        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button
            onClick={() => setMode('text')}
            style={{ padding: '8px 20px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, background: mode === 'text' ? '#1e90ff' : 'rgba(255,255,255,0.1)', color: '#fff', transition: 'background 0.2s' }}
          >
            💬 Texto
          </button>
          <button
            onClick={() => setMode('voice')}
            style={{ padding: '8px 20px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, background: mode === 'voice' ? (hasVoice ? '#7c3aed' : 'rgba(255,255,255,0.1)') : 'rgba(255,255,255,0.1)', color: '#fff', transition: 'background 0.2s', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            🎙 Voz {!hasVoice && <span style={{ fontSize: 10, background: 'rgba(255,200,0,0.25)', borderRadius: 8, padding: '1px 6px', color: '#fcd34d' }}>Premium</span>}
          </button>
        </div>

        {/* Voice mode — locked */}
        {mode === 'voice' && !hasVoice && (
          <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 18, padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Modo Voz — Premium</div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, lineHeight: 1.6, maxWidth: 340, margin: '0 auto 20px' }}>
              Converse em tempo real com {selected.name} usando a sua voz!<br />
              Fale em inglês, a IA entende e responde com a própria voz.
            </div>
            <div style={{ background: 'rgba(255,200,0,0.1)', border: '1px solid rgba(255,200,0,0.25)', borderRadius: 12, padding: '12px 20px', color: '#fcd34d', fontSize: 13, display: 'inline-block' }}>
              ✨ Entre em contato com seu professor para ativar este recurso
            </div>
          </div>
        )}

        {/* Voice mode — active */}
        {mode === 'voice' && hasVoice && (
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, padding: 20, height: 460, display: 'flex', flexDirection: 'column' }}>
            <VoiceMode character={selected} />
          </div>
        )}

        {/* Text mode */}
        {mode === 'text' && (
          <>
            <div style={{
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 18, padding: 20, minHeight: 380, maxHeight: 480, overflowY: 'auto',
              marginBottom: 16, display: 'flex', flexDirection: 'column',
            }}>
              {messages.length === 0 && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', opacity: 0.4, gap: 8 }}>
                  <div style={{ fontSize: 40 }}>{selected.avatar}</div>
                  <div style={{ fontSize: 14 }}>Say hello to {selected.name}!</div>
                </div>
              )}
              {messages.map((m, i) => (
                <ChatMessage key={i} msg={m} character={selected} />
              ))}
              {loading && (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 12 }}>
                  <div style={{ fontSize: 26 }}>{selected.avatar}</div>
                  <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '18px 18px 18px 4px', padding: '14px 18px' }}>
                    <span style={{ display: 'inline-block', animation: 'pulse 1s infinite' }}>✦ ✦ ✦</span>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {messages.length === 0 && (
              <div style={{ background: 'rgba(30,144,255,0.1)', border: '1px solid rgba(30,144,255,0.25)', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
                💡 <strong>Dica:</strong> Escreva em inglês! {selected.name} vai te ajudar se você travar. Use 🔊 para ouvir e 🇧🇷 PT para traduzir.
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder={`Message ${selected.name}...`}
                disabled={loading}
                rows={2}
                style={{
                  flex: 1, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 14, padding: '12px 16px', color: '#fff', fontSize: 15,
                  resize: 'none', outline: 'none', fontFamily: 'inherit',
                }}
              />
              <button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                style={{
                  background: input.trim() && !loading ? grad : 'rgba(255,255,255,0.1)',
                  border: 'none', borderRadius: 14, padding: '0 22px',
                  color: '#fff', fontSize: 20, cursor: input.trim() && !loading ? 'pointer' : 'default',
                  transition: 'background 0.2s', flexShrink: 0,
                }}
              >
                {loading ? '⏳' : '➤'}
              </button>
            </div>
            <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 11, marginTop: 6, textAlign: 'right' }}>
              Enter para enviar · Shift+Enter para nova linha
            </div>
          </>
        )}
      </div>
      <style>{`@keyframes pulse { 0%,100%{opacity:0.3} 50%{opacity:1} }`}</style>
    </div>
  );
}
